import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Search } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";

export const SITE_NAV = [
  { label: "Products", items: [
    ["Personal Credit", "/info/personal-credit", "Monitor, dispute and build."],
    ["Business Credit", "/info/business-credit", "Establish and grow your file."],
    ["Credit Builder", "/products", "Installment and revolving accounts."],
    ["Rent & Bill Reporting", "/products", "Turn payments into history."],
    ["Funding Marketplace", "/marketplace", "Compare capital options."],
    ["Real Estate", "/info/real-estate", "Analyze and invest."],
  ] },
  { label: "Solutions", items: [["For Individuals", "/solutions", "Personal credit and capital."], ["For Businesses", "/solutions", "Business credit and funding."], ["For Investors", "/solutions", "Real estate and opportunities."]] },
  { label: "Membership", to: "/pricing" },
  { label: "Opportunities", to: "/opportunities" },
  { label: "Resources", items: [["Learning", "/learning", "Courses and guides."], ["News & Insights", "/info/news-insights", "What's moving."], ["Financial Education", "/info/financial-education", "Fundamentals."], ["Help Center", "/info/help-center", "Get support."]] },
  { label: "Company", items: [["About", "/info/about", "Who we are."], ["Contact", "/info/contact", "Talk to us."], ["Security & Compliance", "/info/compliance-disclosures", "How we protect you."]] },
];

const tid = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function Dropdown({ group, open, setOpen, palette }) {
  const isOpen = open === group.label;
  return (
    <div className="relative" onMouseEnter={() => setOpen(group.label)} onMouseLeave={() => setOpen(null)}>
      <button type="button" aria-haspopup="menu" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : group.label)} data-testid={`nav-${tid(group.label)}`}
        className={`font-worksans flex items-center gap-1 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#DC6843] ${isOpen ? palette.linkActive : palette.link}`}>
        {group.label} <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div role="menu" data-testid={`nav-menu-${tid(group.label)}`} className={`absolute left-0 top-full z-50 w-[300px] rounded-2xl border p-2 shadow-[0_24px_60px_-24px_rgba(0,0,0,.5)] animate-fade-up ${palette.panel}`}>
          {group.items.map(([l, to, d]) => (
            <Link key={l} to={to} role="menuitem" onClick={() => setOpen(null)} data-testid={`nav-item-${tid(l)}`} className={`flex flex-col rounded-xl px-3 py-2.5 transition-colors focus-visible:outline-none ${palette.panelItem}`}>
              <span className={`font-worksans text-[13.5px] font-semibold ${palette.panelTitle}`}>{l}</span><span className={`text-[12px] ${palette.panelDesc}`}>{d}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function SiteHeader({ primaryTo, tone = "light" }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [acc, setAcc] = useState(null);
  const ref = useRef(null);
  const cta = primaryTo || (user ? "/adaptive" : "/register");
  useEffect(() => { setMobile(false); setOpen(null); }, [pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && (setOpen(null), setMobile(false));
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(null);
    window.addEventListener("keydown", onKey); window.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onClick); };
  }, []);

  const navy = tone === "navy";
  const dark = tone === "dark";
  const palette = navy ? {
    bar: "border-white/10 bg-[#0B1020]/85 text-white",
    logo: "light",
    link: "text-slate-300 hover:text-white",
    linkActive: "text-white",
    search: "text-slate-300 hover:bg-white/10 hover:text-white",
    signin: "border-white/25 text-white hover:bg-white/10",
    ctaBtn: "bg-[#5B4BE6] text-white hover:bg-[#4F46E5]",
    toggle: "text-white hover:bg-white/10",
    panel: "border-slate-200 bg-white",
    panelItem: "hover:bg-slate-50 focus-visible:bg-slate-50",
    panelTitle: "text-slate-900",
    panelDesc: "text-slate-500",
    mobilePanel: "border-white/10 bg-[#0B1020]",
    mobileGroup: "text-white",
    mobileItem: "text-slate-300 hover:bg-white/5",
    mobileBorder: "border-white/10",
    mobileSignin: "border-white/25 text-white",
    mobileCta: "bg-[#5B4BE6] text-white",
  } : dark ? {
    bar: "border-[#1E2D4A] bg-[#050811]/85 text-white",
    logo: "light",
    link: "text-slate-400 hover:text-white", linkActive: "text-white",
    search: "text-slate-400 hover:bg-white/[0.06] hover:text-white",
    signin: "border-white/25 text-white hover:bg-white/[0.06]",
    ctaBtn: "bg-[#2563EB] text-white hover:bg-[#1a5fd0]",
    toggle: "text-white hover:bg-white/[0.06]",
    panel: "border-slate-200 bg-white", panelItem: "hover:bg-slate-50", panelTitle: "text-slate-900", panelDesc: "text-slate-500",
    mobilePanel: "border-slate-200 bg-white", mobileGroup: "text-slate-900", mobileItem: "text-slate-600 hover:bg-slate-50", mobileBorder: "border-slate-100", mobileSignin: "border-slate-300 text-slate-800", mobileCta: "bg-[#0B1220] text-white",
  } : {
    bar: "border-slate-200/80 bg-white/85",
    logo: "dark",
    link: "text-slate-600 hover:text-slate-900", linkActive: "text-slate-900",
    search: "text-slate-500 hover:bg-slate-100 hover:text-slate-900",
    signin: "border-slate-300 text-slate-800 hover:border-slate-400 hover:bg-slate-50",
    ctaBtn: "bg-[#0B1220] text-white hover:bg-[#2563EB]",
    toggle: "text-slate-700 hover:bg-slate-100",
    panel: "border-slate-200 bg-white", panelItem: "hover:bg-slate-50 focus-visible:bg-slate-50", panelTitle: "text-slate-900", panelDesc: "text-slate-500",
    mobilePanel: "border-slate-200 bg-white", mobileGroup: "text-slate-900", mobileItem: "text-slate-600 hover:bg-slate-50", mobileBorder: "border-slate-100", mobileSignin: "border-slate-300 text-slate-800", mobileCta: "bg-[#0B1220] text-white",
  };

  return (
    <header ref={ref} className={`sticky top-0 z-50 border-b backdrop-blur-xl ${palette.bar}`} data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" aria-label="Millionaire Mindsetters Technology — home" data-testid="header-logo" className="shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#DC6843]"><Logo variant={palette.logo} height={26} /></Link>
        <nav aria-label="Primary" className="hidden items-center lg:flex">
          {SITE_NAV.map((g) => g.items ? <Dropdown key={g.label} group={g} open={open} setOpen={setOpen} palette={palette} />
            : <Link key={g.label} to={g.to} data-testid={`nav-${tid(g.label)}`} className={`font-worksans rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#DC6843] ${pathname.startsWith(g.to) ? palette.linkActive : palette.link}`}>{g.label}</Link>)}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/marketplace" aria-label="Search the marketplace" data-testid="nav-search" className={`hidden h-10 w-10 items-center justify-center rounded-lg transition-colors sm:flex ${palette.search}`}><Search size={18} /></Link>
          <Link to="/login" data-testid="header-signin" className={`font-worksans hidden h-10 items-center rounded-xl border px-4 text-[13px] font-semibold transition-colors sm:flex ${palette.signin}`}>Sign In</Link>
          <Link to={cta} data-testid="header-getstarted" className={`font-worksans flex h-10 items-center rounded-xl px-4 text-[13px] font-semibold transition-colors active:scale-[0.98] ${palette.ctaBtn}`}>Get Started</Link>
          <button type="button" onClick={() => setMobile((v) => !v)} aria-label={mobile ? "Close menu" : "Open menu"} aria-expanded={mobile} data-testid="header-mobile-toggle" className={`flex h-10 w-10 items-center justify-center rounded-lg lg:hidden ${palette.toggle}`}>{mobile ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </div>
      {mobile && (
        <nav aria-label="Mobile" data-testid="header-mobile-nav" className={`max-h-[calc(100vh-4rem)] overflow-y-auto border-t px-4 py-3 lg:hidden ${palette.mobilePanel}`}>
          {SITE_NAV.map((g) => g.items ? (
            <div key={g.label} className={`border-b last:border-0 ${palette.mobileBorder}`}>
              <button type="button" onClick={() => setAcc(acc === g.label ? null : g.label)} aria-expanded={acc === g.label} data-testid={`mobile-group-${tid(g.label)}`} className={`font-worksans flex min-h-[48px] w-full items-center justify-between px-2 text-[15px] font-semibold ${palette.mobileGroup}`}>{g.label}<ChevronDown size={16} className={`transition-transform ${acc === g.label ? "rotate-180" : ""}`} /></button>
              {acc === g.label && <div className="pb-2">{g.items.map(([l, to]) => <Link key={l} to={to} data-testid={`mobile-item-${tid(l)}`} className={`flex min-h-[44px] items-center rounded-lg px-4 text-[14px] ${palette.mobileItem}`}>{l}</Link>)}</div>}
            </div>
          ) : <Link key={g.label} to={g.to} data-testid={`mobile-link-${tid(g.label)}`} className={`font-worksans flex min-h-[48px] items-center border-b px-2 text-[15px] font-semibold last:border-0 ${palette.mobileBorder} ${palette.mobileGroup}`}>{g.label}</Link>)}
          <div className="mt-3 flex gap-2"><Link to="/login" className={`font-worksans flex h-11 flex-1 items-center justify-center rounded-xl border text-[14px] font-semibold ${palette.mobileSignin}`}>Sign In</Link><Link to={cta} className={`font-worksans flex h-11 flex-1 items-center justify-center rounded-xl text-[14px] font-semibold ${palette.mobileCta}`}>Get Started</Link></div>
        </nav>
      )}
    </header>
  );
}
